import React, { useState, useEffect, useRef } from 'react';
import { PowerFacility } from '../../../services/powerService';
import { ColumnOption } from '../types';
import ColumnSettings from './ColumnSettings';

interface FacilitiesTableProps {
  facilities: PowerFacility[];
  columnOptions: ColumnOption[];
  visibleColumns: Record<string, boolean>;
  isLoading: boolean;
  showColumnSettings: boolean;
  setShowColumnSettings: (show: boolean) => void;
  toggleColumn: (key: string) => void;
  resetColumnsToDefault: () => void;
}

const getVisibleColumnOptions = (
  columnOptions: ColumnOption[],
  visibleColumns: Record<string, boolean>,
) => {
  return columnOptions.filter((col) => visibleColumns[col.key]);
};

const FacilitiesTable: React.FC<FacilitiesTableProps> = ({
  facilities,
  columnOptions,
  visibleColumns,
  isLoading,
  showColumnSettings,
  setShowColumnSettings,
  toggleColumn,
  resetColumnsToDefault,
}) => {
  const visibleColumnOptions = getVisibleColumnOptions(columnOptions, visibleColumns);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Virtual window pagination - simpler approach
  const itemsPerPage = 50; // Number of items to render at a time
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Calculate total pages
  const totalPages = Math.ceil(facilities.length / itemsPerPage);

  // Get current items to display
  const getCurrentItems = () => {
    const endIndex = currentPage * itemsPerPage;
    return facilities.slice(0, endIndex);
  };

  // Handle scroll to load more
  const handleScroll = () => {
    if (!scrollContainerRef.current || isLoadingMore || currentPage >= totalPages) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const scrolledToBottom = scrollHeight - scrollTop - clientHeight <= 50; // 50px threshold

    if (scrolledToBottom) {
      setIsLoadingMore(true);
      // Use setTimeout to prevent rapid firing
      setTimeout(() => {
        setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages));
        setIsLoadingMore(false);
      }, 300);
    }
  };

  // Reset when facilities change
  useEffect(() => {
    setCurrentPage(1);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [facilities.length]);

  if (!isLoading && facilities.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500 dark:text-gray-400">
          No facilities found matching your criteria.
        </p>
      </div>
    );
  }

  const visibleFacilities = getCurrentItems();

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Results</h3>
        <ColumnSettings
          columnOptions={columnOptions}
          visibleColumns={visibleColumns}
          showColumnSettings={showColumnSettings}
          setShowColumnSettings={setShowColumnSettings}
          toggleColumn={toggleColumn}
          resetColumnsToDefault={resetColumnsToDefault}
        />
      </div>

      <div className="border border-gray-300 dark:border-gray-700 rounded-md">
        <div
          ref={scrollContainerRef}
          className="max-h-[600px] overflow-auto"
          onScroll={handleScroll}
        >
          <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700 table-fixed">
            <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
              <tr>
                {visibleColumnOptions.map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
              {isLoading ? (
                <tr>
                  <td colSpan={visibleColumnOptions.length} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[rgb(227,66,144)]"></div>
                    </div>
                  </td>
                </tr>
              ) : (
                visibleFacilities.map((facility, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    {visibleColumnOptions.map((col) => (
                      <td
                        key={col.key}
                        className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
                      >
                        {facility[col.key as keyof PowerFacility]?.toString() || '-'}
                      </td>
                    ))}
                  </tr>
                ))
              )}

              {/* Loading more indicator */}
              {isLoadingMore && !isLoading && (
                <tr>
                  <td colSpan={visibleColumnOptions.length} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[rgb(227,66,144)]"></div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800 text-sm text-gray-500 dark:text-gray-400 border-t border-gray-300 dark:border-gray-700">
          Showing {visibleFacilities.length} of {facilities.length} facilities
        </div>
      </div>
    </div>
  );
};

export default FacilitiesTable;
